import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { EncryptionService } from '../utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ErrorStrings, eSimApiPaths } from '../../consts';
import { ExceptionsService } from '../utelize/general/exceptions-service';
import { HttpClientService } from '../utelize/general/http-client-service';
import { Injectable } from '@angular/core';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';
import { TravelRiskAdvisoryDTO, TravelRiskAlertDTO, TravelRiskExposureDTO } from '../../models/system/signals/travel-risk-alert.dto';
import { TravelRiskCityReportDTO, TravelRiskCountryReportDTO } from '../../models/system/signals/travel-risk-report.dto';
import { TravelRiskComparisonDTO } from '../../models/system/signals/travel-risk-comparison.dto';
import { TripBriefDTO, TripBriefRequestDTO } from '../../models/system/signals/travel-risk-trip-brief.dto';
import { TravelRiskCountryLevelDTO } from '../../models/system/signals/travel-risk-level.dto';

@Injectable({ providedIn: 'root' })

export class SignalsTravelRiskService {

  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  //#region Private

  private isNotCovered(error: any): boolean {
    try {
      const message: string=(error?.message ?? (typeof error==='string' ? error : '')) ?? '';

      return error?.status===404 || message.includes('404');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  //#endregion

  //#region Global reads (no company scope)

  public getAdvisories(countryIsoCode?: string): Observable<TravelRiskAdvisoryDTO[]> {
    try {
      const query: string=countryIsoCode ? '?countryIsoCode=' + encodeURIComponent(countryIsoCode) : '';

      return this.httpClientService
             .get<TravelRiskAdvisoryDTO[]>(environment.eSimApi
                                           + eSimApiPaths.Signals
                                           + '/'
                                           + eSimApiPaths.SignalsTravelRiskAdvisories
                                           + query)
             .pipe(
               catchError((error) => {
                 return throwError(() => new Error('SignalsTravelRiskService.getAdvisories() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getAlerts(countryIsoCode?: string): Observable<TravelRiskAlertDTO[]> {
    try {
      const query: string=countryIsoCode ? '?countryIsoCode=' + encodeURIComponent(countryIsoCode) : '';

      return this.httpClientService
             .get<TravelRiskAlertDTO[]>(environment.eSimApi
                                        + eSimApiPaths.Signals
                                        + '/'
                                        + eSimApiPaths.SignalsTravelRiskAlerts
                                        + query)
             .pipe(
               catchError((error) => {
                 return throwError(() => new Error('SignalsTravelRiskService.getAlerts() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getCityReport(iataCode: string): Observable<TravelRiskCityReportDTO> {
    try {
      return this.httpClientService
             .get<TravelRiskCityReportDTO>(environment.eSimApi
                                           + eSimApiPaths.Signals
                                           + '/'
                                           + eSimApiPaths.SignalsTravelRiskCityReport
                                           + '?iataCode=' + encodeURIComponent(iataCode))
             .pipe(
               catchError((error) => {
                 if (this.isNotCovered(error))
                   return throwError(() => new Error(ErrorStrings.SignalsNotCovered));

                 return throwError(() => new Error('SignalsTravelRiskService.getCityReport() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCountryLevels(): Observable<TravelRiskCountryLevelDTO[]> {
    try {
      return this.httpClientService
             .get<TravelRiskCountryLevelDTO[]>(environment.eSimApi
                                               + eSimApiPaths.Signals
                                               + '/'
                                               + eSimApiPaths.SignalsTravelRiskCountryRiskLevels)
             .pipe(
               catchError((error) => {
                 return throwError(() => new Error('SignalsTravelRiskService.getCountryLevels() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getCountryReport(countryIsoCode: string): Observable<TravelRiskCountryReportDTO> {
    try {
      return this.httpClientService
             .get<TravelRiskCountryReportDTO>(environment.eSimApi
                                              + eSimApiPaths.Signals
                                              + '/'
                                              + eSimApiPaths.SignalsTravelRiskCountryReport
                                              + '?countryIsoCode=' + encodeURIComponent(countryIsoCode))
             .pipe(
               catchError((error) => {
                 if (this.isNotCovered(error))
                   return throwError(() => new Error(ErrorStrings.SignalsNotCovered));

                 return throwError(() => new Error('SignalsTravelRiskService.getCountryReport() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getComparison(): Observable<TravelRiskComparisonDTO[]> {
    try {
      return this.httpClientService
             .get<TravelRiskComparisonDTO[]>(environment.eSimApi
                                             + eSimApiPaths.Signals
                                             + '/'
                                             + eSimApiPaths.SignalsTravelRiskComparison)
             .pipe(
               catchError((error) => {
                 return throwError(() => new Error('SignalsTravelRiskService.getComparison() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getTripBrief(request: TripBriefRequestDTO): Observable<TripBriefDTO> {
    try {
      return this.httpClientService
             .post(environment.eSimApi
                   + eSimApiPaths.Signals
                   + '/'
                   + eSimApiPaths.SignalsTravelRiskTripBrief,
                   request)
             .pipe(
               catchError((error) => {
                 if (this.isNotCovered(error))
                   return throwError(() => new Error(ErrorStrings.SignalsNotCovered));

                 return throwError(() => new Error('SignalsTravelRiskService.getTripBrief() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#endregion

  //#region Company scoped

  public getExposure(companyId: number): Observable<TravelRiskExposureDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
               switchMap((encResult: StringResultDTO) => {
                 const companyIdEncSS: string=encResult.result;

                 return this.httpClientService
                        .get<TravelRiskExposureDTO[]>(environment.eSimApi
                                                      + eSimApiPaths.Signals
                                                      + '/'
                                                      + eSimApiPaths.SignalsTravelRiskExposure
                                                      + '?companyIdEncSS=' + companyIdEncSS);
               }),
               catchError((error) => {
                 return throwError(() => new Error('SignalsTravelRiskService.getExposure() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  //#endregion
}
